import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, mxn } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { FolderKanban, Plus, Trophy, CheckCircle2, ArrowRight, MapPin, Check, X as XIcon, Users } from "lucide-react";
import { toast } from "sonner";
import { celebrate } from "@/lib/celebrate";

const REGIONS = [
  { value: "centro", label: "Zona Centro" },
  { value: "norte", label: "Zona Norte" },
  { value: "bajio", label: "Bajío" },
  { value: "otros", label: "Otros estados" },
];
const REGION_LABELS = { centro: "Zona Centro", norte: "Zona Norte", bajio: "Bajío", otros: "Otros estados" };

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [talent, setTalent] = useState([]);
  const [impacts, setImpacts] = useState({});
  const [form, setForm] = useState({ name: "", location: "", region: "", collaborator_ids: [] });
  const [collabOpen, setCollabOpen] = useState(false);

  const refresh = async () => {
    const { data: pjs } = await api.get("/projects");
    setProjects(pjs);
    const { data: emps } = await api.get("/employees");
    setTalent(emps);
    // load each project's impact in parallel
    const results = await Promise.all(pjs.map((p) => api.get(`/projects/${p.id}/impact`).then(r => [p.id, r.data]).catch(() => [p.id, null])));
    setImpacts(Object.fromEntries(results));
  };

  useEffect(() => { refresh(); }, []);

  const toggleCollaborator = (id) => {
    setForm((f) => ({
      ...f,
      collaborator_ids: f.collaborator_ids.includes(id)
        ? f.collaborator_ids.filter((x) => x !== id)
        : [...f.collaborator_ids, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (!form.region) { toast.error("Selecciona la zona del proyecto"); return; }
    try {
      await api.post("/projects", form);
      setForm({ name: "", location: "", region: "", collaborator_ids: [] });
      toast.success("Proyecto creado");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falló");
    }
  };

  const closeProject = async (id, name) => {
    try {
      const { data } = await api.post(`/projects/${id}/close`);
      if (data.clean_closeout) {
        celebrate(`Proyecto Cero Desperdicio! '${name}' closed with no unreported surplus.`, { description: "+500 project points." });
      } else {
        toast.warning(data.message);
      }
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falló");
    }
  };

  return (
    <div className="space-y-8" data-testid="projects-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Configuration</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0A2540] tracking-tighter mt-1">Proyectos.</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={submit} className="bg-white border border-[#E2E8F0] rounded-sm p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D]">Crear proyecto</div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Nombre del proyecto</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-11" data-testid="project-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Ubicación</Label>
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-sm h-11" data-testid="project-location-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Zona *</Label>
            <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
              <SelectTrigger className="rounded-sm h-11" data-testid="project-region-select"><SelectValue placeholder="Selecciona una zona" /></SelectTrigger>
              <SelectContent>{REGIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Colaboradores del proyecto</Label>
            <Popover open={collabOpen} onOpenChange={setCollabOpen}>
              <PopoverTrigger asChild>
                <button type="button" data-testid="project-collaborators-trigger"
                  className="w-full flex items-center justify-between rounded-sm h-11 px-3 border border-[#E2E8F0] bg-white text-sm text-left hover:border-[#0A2540]/30 transition-colors">
                  <span className="flex items-center gap-2 text-[#5C6A7D] truncate">
                    <Users size={14} />
                    {form.collaborator_ids.length === 0 ? "Selecciona colaboradores" : `${form.collaborator_ids.length} seleccionado(s)`}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                <Command>
                  <CommandInput placeholder="Buscar en people…" data-testid="project-collaborators-search" />
                  <CommandList>
                    <CommandEmpty>Sin resultados.</CommandEmpty>
                    <CommandGroup>
                      {talent.map((t) => {
                        const selected = form.collaborator_ids.includes(t.id);
                        return (
                          <CommandItem key={t.id} value={t.name} onSelect={() => toggleCollaborator(t.id)} data-testid={`collaborator-option-${t.id}`}>
                            <div className={`mr-2 flex h-4 w-4 items-center justify-center rounded-sm border ${selected ? "bg-[#0A2540] border-[#0A2540] text-white" : "border-[#CBD5E1]"}`}>
                              {selected && <Check size={12} />}
                            </div>
                            <span className="flex-1 truncate">{t.name}</span>
                            {t.position && <span className="text-[10px] uppercase tracking-[0.1em] text-[#5C6A7D] ml-2">{t.position}</span>}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {form.collaborator_ids.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1" data-testid="project-collaborators-chips">
                {form.collaborator_ids.map((id) => {
                  const t = talent.find((x) => x.id === id);
                  if (!t) return null;
                  return (
                    <span key={id} className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-[#EFF5FF] text-[#1D4ED8] font-medium">
                      {t.name}
                      <button type="button" onClick={() => toggleCollaborator(id)} className="hover:text-[#E11D48]" data-testid={`collaborator-remove-${id}`}><XIcon size={12} /></button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
          <Button type="submit" className="w-full bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm h-11" data-testid="create-project-submit">
            <Plus size={14} className="mr-1" /> Crear
          </Button>
        </form>

        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-sm" data-testid="projects-list">
          <div className="px-6 py-4 border-b border-[#E2E8F0] text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D]">Todos los proyectos</div>
          {projects.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-[#5C6A7D]"><FolderKanban className="mx-auto mb-2 text-[#5C6A7D]" /> Aún no hay proyectos.</div>
          ) : (
            <div className="divide-y divide-[#E2E8F0]">
              {projects.map((p) => {
                const imp = impacts[p.id];
                const earned = imp?.earned_badges || [];
                const isClosed = imp?.project?.status === "closed";
                return (
                  <div key={p.id} className="px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-display text-lg font-bold text-[#0A2540]">{p.name}</div>
                        {isClosed && <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm bg-[#5C6A7D]/10 text-[#5C6A7D] border border-[#5C6A7D]/30">Cerrado</span>}
                      </div>
                      <div className="text-xs text-[#5C6A7D] flex items-center flex-wrap gap-x-1 gap-y-0.5 mt-0.5">
                        {p.region && (
                          <span className="inline-flex items-center gap-1 font-semibold text-[#16A34A]" data-testid={`project-region-${p.id}`}>
                            <MapPin size={11} /> {REGION_LABELS[p.region] || p.region}
                          </span>
                        )}
                        {p.region && <span>·</span>}
                        <span>{p.location || "—"} · Gerente: {p.project_manager_name || "Sin asignar"}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {earned.length === 0 && imp && <span className="text-[10px] uppercase tracking-[0.15em] text-[#5C6A7D]">Sin insignias todavía</span>}
                        {earned.map((b) => (
                          <span key={b.key} className="px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm border inline-flex items-center gap-1" style={{ color: b.color, borderColor: `${b.color}40`, background: `${b.color}10` }}>
                            <CheckCircle2 size={10} /> {b.label}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-4">
                      <div>
                        <div className="flex items-center justify-end gap-1 text-[#0A2540]">
                          <Trophy size={14} strokeWidth={1.5} />
                          <span className="font-display font-extrabold text-2xl">{(imp?.points ?? 0).toLocaleString()}</span>
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C6A7D]">{mxn(imp?.money_saved ?? 0)} ahorrados</div>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Link to={`/projects/${p.id}/impact`} className="inline-flex items-center text-xs uppercase tracking-[0.15em] font-bold text-[#0052CC] hover:underline" data-testid={`view-impact-${p.id}`}>
                          Impacto <ArrowRight size={12} className="ml-1" />
                        </Link>
                        {!isClosed && (
                          <button onClick={() => closeProject(p.id, p.name)} className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#5C6A7D] hover:text-[#E11D48]" data-testid={`close-${p.id}`}>
                            Cerrar proyecto
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
